<template lang="html">
	<nav
		class="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border shadow-sm"
	>
		<!-- Main Navigation Bar -->
		<div class="px-3 sm:px-4 lg:px-6 mx-auto max-w-full">
			<div class="flex items-center justify-between h-14 sm:h-16">
				<!-- Left Section: Menu Toggle + Brand -->
				<div class="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
					<!-- Mobile Menu Toggle (Mobile Only) -->
					<button
						v-if="isMobile"
						@click="toggleNav"
						class="p-1.5 sm:p-2 rounded-lg hover:bg-surface active:bg-gray-200 transition-all duration-200"
						:aria-label="isMenuOpen ? 'Close menu' : 'Open menu'"
						aria-controls="mobile-menu"
					>
						<div class="space-y-1.5 navTrigger">
							<span
								class="block w-5 h-0.5 bg-primary transition-all duration-300"
							></span>
							<span
								class="block w-5 h-0.5 bg-primary transition-all duration-300"
							></span>
							<span
								class="block w-5 h-0.5 bg-primary transition-all duration-300"
							></span>
						</div>
					</button>

					<!-- Sidebar Toggle (Desktop Only) -->
					<button
						v-else
						@click="toggleSidebar"
						class="p-1.5 sm:p-2 rounded-lg hover:bg-surface active:bg-gray-200 transition-all duration-200 group"
						:aria-label="
							isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
						"
					>
						<svg
							class="w-5 h-5 text-text-primary group-hover:text-primary transition-colors"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								:d="
									isCollapsed
										? 'M13 5l7 7-7 7M5 5l7 7-7 7'
										: 'M11 19l-7-7 7-7m8 14l-7-7 7-7'
								"
							/>
						</svg>
					</button>

					<!-- Brand Logo -->
					<router-link
						:to="{ name: 'Home' }"
						class="flex items-center space-x-1.5 sm:space-x-2 group"
					>
						<div class="relative">
							<div
								class="absolute inset-0 bg-brand-cyan/20 rounded-lg blur-sm"
							></div>
							<img
								src="/assets/Logo/nexus.svg"
								alt="Nexus"
								class="h-6 sm:h-8 w-auto object-contain relative z-10 transition-transform group-hover:scale-105"
							/>
						</div>
						<div class="flex flex-col">
							<span
								class="text-sm sm:text-lg font-bold text-text-primary tracking-tight"
								>Nexus</span
							>
							<span class="text-xs font-medium text-primary hidden xs:inline sm:inline"
								>Insurance Portal</span
							>
						</div>
					</router-link>
					<!-- Welcome Message (Desktop - Large screens) -->
					<div
						v-if="isAuthenticated && userName"
						class="hidden xl:flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-primary/5 to-brand-cyan/5 rounded-lg border border-primary/10"
					>
						<span class="text-xs text-text-secondary"
							>Welcome back,</span
						>
						<span class="text-sm font-semibold text-primary">{{
							userName
						}}</span>
					</div>
				</div>

				<!-- Right Section: Actions & User Info -->
				<div class="flex items-center space-x-1.5 sm:space-x-2 lg:space-x-3">
					<!-- New Quote Button -->
					<button
						@click="navigateNewQuote"
						class="relative group flex items-center gap-2 bg-primary text-white px-4 sm:px-6 py-2.5 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 active:scale-95 overflow-hidden"
					>
						<!-- Shimmer effect -->
						<div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
						
						<!-- Button content -->
						<span class="relative z-10 text-sm sm:text-base font-medium">New Quote</span>
						<svg class="relative z-10 w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
						</svg>
					</button>

					<!-- Invoice Summary (Desktop Only) -->
					<div
						v-if="isAuthenticated"
						class="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-surface rounded-lg"
					>
						<div class="flex items-center gap-1.5">
							<div
								class="flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-semibold text-white bg-accent rounded-md cursor-help transition-transform hover:scale-105"
								:title="`${getUnpaidInvoices()} Unpaid Invoice${getUnpaidInvoices() !== 1 ? 's' : ''}`"
							>
								{{ getUnpaidInvoices() }}
							</div>
							<span
								class="text-xs text-text-secondary hidden lg:inline"
								>Unpaid</span
							>
						</div>

						<div class="w-px h-4 bg-border"></div>

						<div class="flex items-center gap-1.5">
							<div
								class="flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-semibold text-white bg-green-500 rounded-md cursor-help transition-transform hover:scale-105"
								:title="`${getPaidInvoices()} Paid Invoice${getPaidInvoices() !== 1 ? 's' : ''}`"
							>
								{{ getPaidInvoices() }}
							</div>
							<span
								class="text-xs text-text-secondary hidden lg:inline"
								>Paid</span
							>
						</div>
					</div>


					<!-- Divider (Desktop Only) -->
					<div
						v-if="isAuthenticated"
						class="hidden lg:block h-8 w-px bg-border"
					></div>

					<!-- Action Buttons Group -->
					<div class="flex items-center space-x-1.5 lg:space-x-2">
						<!-- Notifications -->
						<!--            <router-link-->
						<!--                v-if="isAuthenticated"-->
						<!--                :to="{ name: 'Notification' }"-->
						<!--                class="relative p-2 rounded-lg hover:bg-surface active:bg-gray-200 transition-all duration-200 group"-->
						<!--                title="Notifications"-->
						<!--                aria-label="View notifications"-->
						<!--            >-->
						<!--              <svg class="w-5 h-5 text-text-primary group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">-->
						<!--                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />-->
						<!--              </svg>-->
						<!--              <span class="absolute top-1 right-1 flex h-2 w-2">-->
						<!--                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>-->
						<!--                <span class="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>-->
						<!--              </span>-->
						<!--            </router-link>-->

						<!-- Help Button -->
						<button
							@click="openHelp"
							class="flex items-center space-x-1.5 px-3 lg:px-4 py-2 text-sm font-medium text-white bg-accent hover:bg-brand-cyan/90 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 active:scale-95"
							title="Get help and support"
						>
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
							<span class="hidden sm:inline">Help</span>
						</button>

						<!-- Logout Button (Desktop Only) -->
						<button
							v-if="isAuthenticated"
							@click="logOut"
							class="hidden lg:flex items-center space-x-1.5 px-3 lg:px-4 py-2 text-sm font-medium text-text-primary hover:text-accent hover:bg-surface rounded-lg transition-all duration-200 group"
							title="Log out of your account"
						>
							<svg
								class="w-4 h-4 transition-transform group-hover:translate-x-0.5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
								/>
							</svg>
							<span class="hidden lg:inline">Logout</span>
						</button>

						<!-- Profile Avatar -->
						<!--            <button-->
						<!--                v-if="isAuthenticated"-->
						<!--                class="relative group"-->
						<!--                title="View profile"-->
						<!--                aria-label="User profile menu"-->
						<!--            >-->
						<!--              <div class="relative">-->
						<!--                <div class="absolute inset-0 bg-primary/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur"></div>-->
						<!--                <div-->
						<!--                    class="relative w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary-light flex items-center justify-center text-white font-bold text-sm border-2 border-white shadow-md group-hover:scale-105 transition-transform duration-200"-->
						<!--                >-->
						<!--                  {{ userInitials }}-->
						<!--                </div>-->
						<!--                <div class="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>-->
						<!--              </div>-->
						<!--            </button>-->

						<!-- Login Button (Guest) -->
						<button
							v-else
							@click="logIn"
							class="flex items-center space-x-2 px-4 lg:px-5 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary-light rounded-lg shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
						>
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
								/>
							</svg>
							<span>Log In</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	</nav>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { FetchIntermediaries } from '../insurance/api/insurance';

export default {
	name: 'DashNav',

	props: {
		isMenuOpen: {
			type: Boolean,
			default: false,
		},
		isCollapsed: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			isMobile: false,
			resizeTimeout: null,
		};
	},

	computed: {
		...mapGetters('auth', ['isAuthenticated', 'selectAuthData']),
		...mapGetters('kyc', ['selectKycData']),
		...mapGetters('invoices', ['selectInvoicesData']),

		userInitials() {
			const name = this.userName;
			return name.charAt(0).toUpperCase();
		},

		userName() {
			const kycName =
				this.selectKycData &&
				this.selectKycData.names &&
				this.selectKycData.names.firstName;
			const authName =
				this.selectAuthData && this.selectAuthData.firstName;
			return kycName || authName || 'User';
		},
	},

	methods: {
		...mapActions('auth', ['logout']),
		...mapActions('help', ['setEntities']),
		...mapActions('risk', ['clearRiskData']),
		...mapActions('quotation', ['resetQuotationModule']),

		handleResize() {
			clearTimeout(this.resizeTimeout);
			this.resizeTimeout = setTimeout(() => {
				this.isMobile = window.innerWidth < 768;
			}, 100);
		},

		toggleNav() {
			this.$emit('toggle-mobile-menu');
		},

		toggleSidebar() {
			this.$emit('toggle-sidebar');
		},
		navigateNewQuote() {
			this.clearRiskData();
			this.resetQuotationModule();
			this.$router.push({ name: 'Home' });
		},

		updateHamburger(isOpen) {
			this.$nextTick(() => {
				const lines = this.$el.querySelectorAll('.navTrigger span');
				if (lines.length === 3) {
					if (isOpen) {
						lines[0].style.transform =
							'rotate(45deg) translate(5px, 5px)';
						lines[1].style.opacity = '0';
						lines[2].style.transform =
							'rotate(-45deg) translate(7px, -7px)';
					} else {
						lines[0].style.transform = 'none';
						lines[1].style.opacity = '1';
						lines[2].style.transform = 'none';
					}
				}
			});
		},

		async openHelp() {
			try {
				const res = await FetchIntermediaries();
				if (res.status) {
					this.setEntities(res.data);
					this.$router.push({ name: 'HelpMotor' });
				}
			} catch (error) {
				console.error('Error fetching intermediaries:', error);
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: 'Unable to open help center',
					duration: 5000,
				});
			}
		},

		logIn() {
			this.$router.push({ name: 'Login' });
		},

		async logOut() {
			try {
				await this.logout();
				sessionStorage.clear();
				this.$router.push({ name: 'Home' });
			} catch (error) {
				console.error('Logout failed:', error);
			}
		},

		getUnpaidInvoices() {
			return this.selectInvoicesData.filter(
				(invoice) => invoice.invoiceStatus === 'PENDING-PAYMENT',
			).length;
		},

		getPaidInvoices() {
			return this.selectInvoicesData.filter(
				(invoice) => invoice.invoiceStatus === 'PAID',
			).length;
		},
	},

	watch: {
		isMenuOpen(newVal) {
			this.updateHamburger(newVal);
		},
	},

	mounted() {
		this.isMobile = window.innerWidth < 768;
		window.addEventListener('resize', this.handleResize);
		this.updateHamburger(this.isMenuOpen);
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize);
		clearTimeout(this.resizeTimeout);
	},
};
</script>

<style lang="less" scoped>
/* Hamburger menu animation */
.navTrigger span {
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Fallback for browsers without backdrop-filter support */
@supports not (backdrop-filter: blur(12px)) {
	nav {
		background-color: rgba(255, 255, 255, 0.98);
	}
}

/* Smooth transitions for interactive elements */
button,
a {
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Enhanced focus states for accessibility */
button:focus-visible,
a:focus-visible {
	outline: 2px solid theme('colors.primary.DEFAULT');
	outline-offset: 2px;
}

/* Smooth shadow transition on scroll */
nav {
	transition: box-shadow 0.3s ease;
}
</style>
